import { Issue, Sprint, User, UserAllocation } from '@/types';

export function calculateUserAllocations(issues: Issue[], sprints: Sprint[]): UserAllocation[] {
  console.log(`Calculating allocations for ${issues.length} issues across ${sprints.length} sprints`);
  
  // Group issues by user
  const userMap = new Map<string, User>();
  const userIssues = new Map<string, Issue[]>();
  
  // Collect all users and their issues
  issues.forEach(issue => {
    if (issue.assignee) {
      const userId = issue.assignee.id;
      
      // Store user
      if (!userMap.has(userId)) {
        userMap.set(userId, issue.assignee);
        userIssues.set(userId, []);
      }
      
      // Add issue to user's issues
      userIssues.get(userId)?.push(issue);
    }
  });
  
  console.log(`Found ${userMap.size} users with assigned issues`);
  
  // Calculate allocations for each user
  const userAllocations: UserAllocation[] = [];
  
  userMap.forEach((user, userId) => {
    // Include users with @wapp.gmbh email OR with "Stickan" in their name
    const hasWappEmail = user.emailAddress && user.emailAddress.endsWith('@wapp.gmbh');
    const hasStickanInName = user.displayName && user.displayName.includes('Stickan');
    
    if (!hasWappEmail && !hasStickanInName) {
      console.log(`Skipping user ${user.displayName} with email ${user.emailAddress}`);
      return;
    }
    
    const userIssueList = userIssues.get(userId) || [];
    console.log(`User ${user.displayName} has ${userIssueList.length} issues`);
    
    const sprintAllocations: { 
      [sprintId: string]: {
        qaStoryPoints: number; 
        storyPoints: number; 
        completedStoryPoints: number;
        issues: Issue[] 
      } 
    } = {};
    
    let totalStoryPoints = 0;
    let totalCompletedStoryPoints = 0;
    let totalQaStoryPoints = 0;
    
    // Initialize sprints
    sprints.forEach(sprint => {
      sprintAllocations[sprint.id] = { 
        storyPoints: 0, 
        completedStoryPoints: 0,
        qaStoryPoints: 0,
        issues: [] 
      };
    });

    const uniqueIssueStates: (string | undefined)[] = []

    // Calculate story points per sprint
    for (const issue of userIssueList) {
      if (issue.sprint) {
        const sprintId = issue.sprint.id;
        
        if (!sprintAllocations[sprintId]) {
          sprintAllocations[sprintId] = { 
            storyPoints: 0, 
            completedStoryPoints: 0,
            qaStoryPoints: 0,
            issues: [] 
          };
        }
        
        // Always add the issue to the sprint, even if it has 0 story points
        sprintAllocations[sprintId].issues.push(issue);
        
        // Only add story points if they exist
        if (issue.storyPoints) {
          sprintAllocations[sprintId].storyPoints += issue.storyPoints;
          totalStoryPoints += issue.storyPoints;
          
          // Track completed story points separately
          if (issue.done) {
            sprintAllocations[sprintId].completedStoryPoints += issue.storyPoints;
            totalCompletedStoryPoints += issue.storyPoints;
          }

          if (!uniqueIssueStates.includes(issue.status)) {
            uniqueIssueStates.push(issue.status)
          }

          // Track QA and READY TO DEPLOY story points
          if (issue.status && ['QS', 'READY TO DEPLOY'].includes(issue.status.toUpperCase())) {
            sprintAllocations[sprintId].qaStoryPoints += issue.storyPoints;
            totalQaStoryPoints += issue.storyPoints;
          }
        }
      }
    }

    console.log(`Unique issue states: ${uniqueIssueStates.join(', ')}`)
    
    userAllocations.push({
      user,
      sprints: sprintAllocations,
      totalStoryPoints,
      totalCompletedStoryPoints,
      totalQaStoryPoints
    });
  });
  
  console.log(`Returning ${userAllocations.length} user allocations`);
  
  // Sort alphabetically by user display name
  return userAllocations.sort((a, b) => 
    a.user.displayName.localeCompare(b.user.displayName)
  );
}